/**
 * Proposal List Page Component
 *
 * View and manage all proposals
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { advisorClient } from '../api/client';
import { AdvisorServiceMissingError, AdvisorServiceNotice, advisorFailure } from '../components/AdvisorServiceNotice';
import type { components } from '@aaimplatform/institutional-types/advisor-tools';
import {
  Heading,
  BodyText,
  Card,
  Button,
  MetricDisplay,
} from '@aaimplatform/ui';


type ProposalSummary = components['schemas']['ProposalSummary'];

export function ProposalListPage() {
  const { data: proposals, isLoading, error } = useQuery<ProposalSummary[]>({
    queryKey: ['proposals'],
    queryFn: async () => {
      const { data, error, response } = await advisorClient.GET('/api/proposals');
      if (error || !response.ok) throw advisorFailure(response, error, 'Failed to load proposals');
      return data;
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'text-tertiary bg-recessed border-surface';
      case 'SENT':
        return 'text-accent bg-accent/5 border-accent/20';
      case 'ACCEPTED':
        return 'text-growth bg-growth/10 border-growth/20';
      case 'REJECTED':
        return 'text-error bg-error/10 border-error/20';
      case 'EXPIRED':
        return 'text-tertiary bg-recessed border-surface';
      default:
        return 'text-primary bg-surface border-recessed';
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <Link to="/proposals/new">
          <Button variant="primary">Create New Proposal</Button>
        </Link>
      </div>

      {isLoading ? (
        <Card>
          <BodyText className="text-secondary text-center py-8">Loading proposals...</BodyText>
        </Card>
      ) : error instanceof AdvisorServiceMissingError ? (
        <AdvisorServiceNotice />
      ) : proposals && proposals.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {proposals.map((proposal) => (
            <Card key={proposal.id}>
              <div className="flex justify-between items-start mb-4">
                <Heading level={3} as={3} size="label">{proposal.clientName}</Heading>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(proposal.status)}`}
                >
                  {proposal.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center py-2 border-b border-surface">
                  <BodyText className="text-tertiary text-sm">Loan Amount</BodyText>
                  <MetricDisplay value={formatCurrency(proposal.loanAmount)} className="text-primary" />
                </div>
                <div className="flex justify-between items-center py-2 border-b border-surface">
                  <BodyText className="text-tertiary text-sm">Interest Rate</BodyText>
                  <BodyText className="font-mono font-semibold text-primary">
                    {proposal.interestRate.toFixed(2)}% APR
                  </BodyText>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-surface">
                  <BodyText className="text-tertiary text-sm">Term</BodyText>
                  <BodyText className="font-mono text-primary">{proposal.termMonths} months</BodyText>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-surface">
                  <BodyText className="text-tertiary text-sm">Created</BodyText>
                  <BodyText className="text-primary text-sm">{formatDate(proposal.createdAt)}</BodyText>
                </div>
                {proposal.sentAt && (
                  <div className="flex justify-between items-center py-2 border-b border-surface">
                    <BodyText className="text-tertiary text-sm">Sent</BodyText>
                    <BodyText className="text-primary text-sm">{formatDate(proposal.sentAt)}</BodyText>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <Link to={`/proposals/${proposal.id}`} className="flex-1">
                  <Button variant="secondary" className="w-full">
                    View/Edit
                  </Button>
                </Link>
                {proposal.status === 'DRAFT' && (
                  <Button variant="primary" className="flex-1">
                    Send to Client
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="text-center py-12">
            <BodyText className="text-secondary mb-4">No proposals yet</BodyText>
            <Link to="/proposals/new">
              <Button variant="primary">Create Your First Proposal</Button>
            </Link>
          </div>
        </Card>
      )}
    </div>
  );
}
